<template>
    <div class="select-input-container">
        <div class="select-input-wrapper">
            <BasicDropdown
                name="country-flag"
                class="select"
                v-model="selectedCountry"
                :options="countriesList"
                :disabled="['0']"
                style="border-radius: 10px 0 0 10px"
                alignement="center"
                :dataCy="`${dataCy}_select`"
                @update:modelValue="updateSelectedCountry($event)"
            >
                <template #selected>
                    <div class="flag">
                        <div class="image-wrapper">
                            <img :src="selectedCountry.flag" />
                        </div>
                    </div>
                    {{ selectedCountry.name }}
                </template>
                <template #options="{ flag, name }">
                    <div class="options-flag">
                        <div class="image-wrapper">
                            <img :src="flag" />
                        </div>
                        {{ name }}
                    </div>
                </template>
            </BasicDropdown>
            <BasicInput
                class="input"
                id="name"
                name="phone-number"
                v-model="phoneNumber"
                :data-cy="`${dataCy}_input`"
                :disabled="loading"
            />
        </div>
    </div>
</template>

<script>
import BasicDropdown from "@/components/basic/BasicDropdown.vue"
import BasicInput from "@/components/basic/BasicInput.vue"

import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js/max"

export default {
    components: {
        BasicDropdown,
        BasicInput,
    },

    data() {
        return {
            countriesList: [],
            selectedCountry: {},
        }
    },

    emits: [
        "error:parsePhoneNumber",
        "update:modelValue",
        "update:isValidPhone",
    ],

    props: {
        modelValue: {
            type: Object,
            default: () => ({
                code: "",
                number: "",
            }),
        },
        countries: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: null,
        },
        dataCy: {
            type: String,
            default: null,
        },
    },

    computed: {
        phoneNumber: {
            get() {
                if (!this.modelValue?.number || !this.selectedCountry?.value)
                    return ""
                if (this.selectedCountry?.id === 0) {
                    return this.modelValue.number
                }

                try {
                    return parsePhoneNumber(
                        `${this.selectedCountry.name}${this.modelValue.number}`,
                        this.selectedCountry.value,
                    ).formatNational()
                } catch {
                    return this.modelValue.number
                }
            },

            set(value) {
                let cleanNumber = value

                if (this.selectedCountry?.id !== 0) {
                    try {
                        cleanNumber = parsePhoneNumber(
                            value,
                            this.selectedCountry.value,
                        )
                            .formatNational()
                            .replace(/\s+/g, "")
                    } catch {
                        cleanNumber = value
                    }
                }

                this.validPhone(cleanNumber)
                this.updateModelValue(cleanNumber)
            },
        },

        whatIsLanguage() {
            if (parseInt(this.$store.getters["residenceInfos/getCountry"])) {
                return this.$store.getters["residenceInfos/getCountry"]
            } else if (parseInt(this.$store.getters["account/getCountry"])) {
                return this.$store.getters["account/getCountry"]
            } else {
                // FR id by default
                return "1"
            }
        },
    },

    methods: {
        resolveDefaultCountry() {
            return (
                this.countries.find(
                    (country) => country.id === String(this.whatIsLanguage),
                ) ??
                this.countries.find((country) => country.id === "1") ??
                this.countries[0]
            )
        },

        parsePhoneNumber(phoneObject) {
            try {
                if (phoneObject.code && phoneObject.number) {
                    let parsedPhoneNumber

                    // If number is registered like "+33123..." in BDD
                    if (phoneObject.number.startsWith("+")) {
                        parsedPhoneNumber = parsePhoneNumber(phoneObject.number)
                    } else if (phoneObject.code.startsWith("+")) {
                        parsedPhoneNumber = parsePhoneNumber(
                            `${phoneObject.code}${phoneObject.number}`,
                        )
                    } else {
                        parsedPhoneNumber = parsePhoneNumber(
                            `+${phoneObject.code}${phoneObject.number}`,
                        )
                    }

                    this.selectedCountry = this.countries.find(
                        (country) =>
                            country.name ===
                            `+${parsedPhoneNumber.countryCallingCode}`,
                    )

                    this.phoneNumber = parsedPhoneNumber.nationalNumber
                } else {
                    this.selectedCountry = this.resolveDefaultCountry()
                }
            } catch (error) {
                switch (error.message) {
                    case "INVALID_COUNTRY": {
                        const unknownOption = {
                            id: 0,
                            name: "+?",
                            flag: "./img/flags/unknown.svg",
                            value: "0",
                        }

                        this.countriesList.unshift(unknownOption)

                        this.selectedCountry = this.countriesList.find(
                            (country) => country.id === 0,
                        )

                        break
                    }
                }
                this.$emit("error:parsePhoneNumber", error.message)
            }
        },

        updateSelectedCountry(country) {
            this.selectedCountry = country

            let cleanNumber = this.phoneNumber

            // Nettoyage du cas legacy (+0 ou 0)
            if (cleanNumber.startsWith("+0")) {
                cleanNumber = cleanNumber.replace("+0", "")
            } else if (cleanNumber.startsWith("0")) {
                cleanNumber = cleanNumber.substring(1)
            }

            this.validPhone(this.phoneNumber)
            this.updateModelValue(cleanNumber)
        },

        validPhone(phoneNumber) {
            const isValid = isValidPhoneNumber(
                phoneNumber,
                this.selectedCountry.value,
            )

            this.$emit("update:isValidPhone", isValid)
        },

        updateModelValue(newModelValue) {
            // Delete white spaces isnerted by Libphone
            const formatedNewModelValue = newModelValue.replace(/\s/g, "")
            // Delete "+" from country code, DB doesn't want it
            const formatedCountryCode = this.selectedCountry?.name.split("+")[1]

            this.$emit("update:modelValue", {
                code: formatedCountryCode,
                number: formatedNewModelValue,
            })
        },
    },

    mounted() {
        this.parsePhoneNumber(this.modelValue)
    },

    watch: {
        countries: {
            handler(newList) {
                // In the case if countries is refreshed by parent, we need to secure it

                // Check if unknow country was in array
                const unknownOption = this.countriesList.find((c) => c.id === 0)

                // Update array with potential new data
                let updatedList = newList.map((item) => ({ ...item }))

                // If there was unknown option, re-push it in updated array
                if (unknownOption) {
                    updatedList = [unknownOption, ...updatedList]

                    // Update selectedCountry if his value was the unknown option
                    if (this.selectedCountry?.id === 0) {
                        this.selectedCountry = updatedList[0]
                    }
                }

                this.countriesList = updatedList

                if (!this.selectedCountry?.value && !this.modelValue?.number) {
                    this.selectedCountry = this.resolveDefaultCountry()
                }
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>

<style lang="scss" scoped>
.select-input-container {
    display: flex;
    width: 100%;

    .select-input-wrapper {
        width: 100%;
        display: flex;
        .select {
            max-width: 120px;
            height: 100%;

            .flag {
                display: flex;
                align-items: center;
                gap: 0.5em;

                .image-wrapper {
                    width: 20px;
                    height: 15px;
                    border-radius: 3px;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        display: flex;
                    }
                }
            }
        }

        .input {
            width: 100%;
        }
    }
}
</style>

<style lang="scss">
@import "@/assets/scss/global.scss";
@import "@/assets/scss/_loader.scss";

.options-flag {
    display: flex;
    align-items: center;
    gap: 0.5em;

    .image-wrapper {
        width: 20px;
        height: 15px;
        border-radius: 3px;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: flex;
        }
    }
}

.add-button {
    display: flex;
    align-items: center;
    gap: 0.5em;
    color: $blue-neutral;
    font-family: $font-avenir-medium;
}
</style>
